// ─── src/chat/BulkEnquiryModal.jsx ───────────────────────────────────────────
// Aura bulk enquiry, send one brief to up to 3 shortlisted venues in parallel.
//
// Anti-spam rules (enforced client-side):
//   - 1 bulk submission per hour per session
//   - Max 3 total submissions per session (bulk or single)
//   - Minimum 40-char message body
//   - Duplicate detection: same listing_id cannot be enquired twice per session
//
// Each venue receives a personalised intro + the shared brief.
// Submissions are parallel (Promise.allSettled) with partial success handling.

import { useState, useRef } from "react";
import { createLead }        from "../services/leadEngineService";
import { getEnquiryAttributionPayload } from "../stores/auraCore";
import { useAura }           from "../stores/auraCore";
import Icon                  from "./Icons";
import { imgSrc }            from "../utils/transformListing";

const GOLD = "#d39a21";

// ─── Anti-spam ────────────────────────────────────────────────────────────────

const SPAM_KEY = "lwd:aura:enquiry_log";

function loadLog() {
  try { return JSON.parse(sessionStorage.getItem(SPAM_KEY) ?? "null") ?? { submissions: [], lastBulkAt: null }; }
  catch { return { submissions: [], lastBulkAt: null }; }
}

function saveLog(log) {
  try { sessionStorage.setItem(SPAM_KEY, JSON.stringify(log)); } catch {}
}

function passesAntiSpam(listingIds) {
  const log = loadLog();
  const now = Date.now();

  // Max 3 total submissions per session
  if (log.submissions.length >= 3) {
    return { ok: false, reason: "You've sent 3 enquiries in this session. Please contact venues directly for further enquiries." };
  }

  // 1 bulk per hour
  if (log.lastBulkAt && (now - log.lastBulkAt) < 60 * 60 * 1000) {
    const minLeft = Math.ceil((60 * 60 * 1000 - (now - log.lastBulkAt)) / 60000);
    return { ok: false, reason: `Please wait ${minLeft} minute${minLeft !== 1 ? "s" : ""} before sending another bulk enquiry.` };
  }

  // Duplicate detection
  const alreadySent = listingIds.filter((id) => log.submissions.includes(id));
  if (alreadySent.length > 0) {
    return { ok: false, reason: "One or more of these venues has already received your enquiry in this session." };
  }

  return { ok: true };
}

function logSubmission(listingIds) {
  const log = loadLog();
  log.submissions = [...new Set([...log.submissions, ...listingIds])];
  log.lastBulkAt = Date.now();
  saveLog(log);
}

// ─── Theme ────────────────────────────────────────────────────────────────────

function getT(dark) {
  if (dark) return {
    overlay:    "rgba(0,0,0,0.72)",
    bg:         "#1A1816",
    border:     "rgba(211,154,33,0.16)",
    text:       "#F5F0E8",
    subtext:    "rgba(245,240,232,0.5)",
    inputBg:    "rgba(255,255,255,0.04)",
    inputBorder:"rgba(211,154,33,0.18)",
    inputText:  "rgba(255,255,255,0.9)",
    placeholder:"rgba(255,255,255,0.3)",
    divider:    "rgba(255,255,255,0.07)",
    labelColor: "rgba(245,240,232,0.55)",
    errColor:   "#f87171",
  };
  return {
    overlay:    "rgba(0,0,0,0.55)",
    bg:         "#FDFBF8",
    border:     "rgba(211,154,33,0.18)",
    text:       "#1A1714",
    subtext:    "rgba(26,23,20,0.5)",
    inputBg:    "#F8F5F0",
    inputBorder:"rgba(26,23,20,0.15)",
    inputText:  "#1A1714",
    placeholder:"rgba(26,23,20,0.3)",
    divider:    "rgba(26,23,20,0.08)",
    labelColor: "rgba(26,23,20,0.55)",
    errColor:   "#dc2626",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BulkEnquiryModal({ items, onClose, darkMode = true }) {
  const T = getT(darkMode);
  const markVenueEnquired = useAura((s) => s.markVenueEnquired);

  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [date,    setDate]    = useState("");
  const [guests,  setGuests]  = useState("");
  const [message, setMessage] = useState("");
  const [errors,  setErrors]  = useState({});
  const [status,  setStatus]  = useState("idle"); // idle | submitting | success | partial | error
  const [resultText, setResultText] = useState("");

  const overlayRef = useRef(null);

  const GUEST_OPTIONS = ["Under 30", "30–60", "60–120", "120–200", "200+"];

  // ── Validation ──────────────────────────────────────────────────────────────
  function validate() {
    const e = {};
    if (!name.trim())                      e.name    = "Name is required";
    if (!email.trim() || !email.includes("@")) e.email = "Valid email is required";
    if (!guests)                           e.guests  = "Guest count is required";
    if (message.trim().length < 40)        e.message = `Please add a bit more detail (${message.trim().length}/40 chars minimum)`;
    return e;
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    // Anti-spam check
    const listingIds = items.map((v) => v.id);
    const spamCheck  = passesAntiSpam(listingIds);
    if (!spamCheck.ok) {
      setErrors({ submit: spamCheck.reason });
      return;
    }

    setStatus("submitting");
    setErrors({});

    // Build attribution context
    const auraAttrib = getEnquiryAttributionPayload();

    // Fire enquiries in parallel, one per venue
    const results = await Promise.allSettled(
      items.map((venue) =>
        createLead({
          leadSource:      "aura_chat",
          leadChannel:     "chat",
          leadType:        "venue_enquiry",
          listingId:       venue.id,
          listingType:     "venue",
          fullName:        name.trim(),
          email:           email.trim(),
          weddingDate:     date || null,
          guestCount:      guests,
          message:         `We came across ${venue.name} through our search on Luxury Wedding Directory.\n\n${message.trim()}`,
          auraSessionId:   auraAttrib.auraSessionId,
          intentSummary:   auraAttrib.intentSummary ?? undefined,
        })
      )
    );

    const succeeded = results.filter((r) => r.status === "fulfilled" && r.value?.success);
    const failed    = results.filter((r) => r.status === "rejected"  || !r.value?.success);

    // Log to anti-spam store
    logSubmission(listingIds);

    // Mark each successfully submitted venue as enquired.
    // Buying-signal chips and the enquiry popup are suppressed for these IDs
    // until the user focuses a different venue or starts a new conversation.
    items
      .filter((_, i) => results[i].status === "fulfilled" && results[i].value?.success)
      .forEach((v) => markVenueEnquired(v.id));

    if (succeeded.length === items.length) {
      setStatus("success");
      const venueNames = items.map((v) => v.name);
      const nameList = venueNames.length === 1
        ? venueNames[0]
        : venueNames.length === 2
        ? `${venueNames[0]} and ${venueNames[1]}`
        : `${venueNames[0]}, ${venueNames[1]}, and ${venueNames[2]}`;
      setResultText(`Your brief has been sent to ${nameList}. Each team will respond within 24 hours.`);
    } else if (succeeded.length > 0) {
      setStatus("partial");
      const succNames = items
        .filter((_, i) => results[i].status === "fulfilled" && results[i].value?.success)
        .map((v) => v.name);
      setResultText(`Sent to ${succNames.join(", ")}. ${failed.length} venue${failed.length > 1 ? "s" : ""} could not be reached. Please try again or contact them directly.`);
    } else {
      setStatus("error");
      setResultText("We couldn't send your enquiry right now. Please try again in a moment.");
    }
  }

  // ── Close on overlay click ──────────────────────────────────────────────────
  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const isComplete = status === "success" || status === "partial";

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position:       "fixed",
        inset:          0,
        background:     T.overlay,
        zIndex:         9999,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "20px",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          background:   T.bg,
          border:       `1px solid ${T.border}`,
          borderRadius: 12,
          width:        "100%",
          maxWidth:     440,
          maxHeight:    "90vh",
          overflowY:    "auto",
          boxShadow:    "0 24px 80px rgba(0,0,0,0.35)",
          position:     "relative",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position:       "absolute",
            top:            14,
            right:          14,
            width:          28,
            height:         28,
            borderRadius:   "50%",
            background:     "rgba(255,255,255,0.06)",
            border:         `1px solid ${T.divider}`,
            color:          T.subtext,
            cursor:         "pointer",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            fontSize:       14,
            zIndex:         1,
          }}
        >
          ×
        </button>

        {/* ── Success / partial / error state ──────────────────────────────── */}
        {(isComplete || status === "error") ? (
          <div style={{ padding: "48px 32px", textAlign: "center" }}>
            <div style={{ marginBottom: 16 }}>
              {isComplete
                ? <Icon name="checkCircle" size={40} color={GOLD} />
                : <Icon name="sparkle"     size={40} color={T.errColor} />}
            </div>
            <div style={{
              fontFamily:   "var(--font-heading-primary)",
              fontSize:     22,
              color:        T.text,
              marginBottom: 12,
              lineHeight:   1.3,
            }}>
              {isComplete ? "Enquiry Sent" : "Something went wrong"}
            </div>
            <div style={{
              fontFamily: "var(--font-body)",
              fontSize:   13.5,
              color:      T.subtext,
              lineHeight: 1.6,
              marginBottom: 28,
            }}>
              {resultText}
            </div>
            <button
              onClick={onClose}
              style={{
                padding:       "10px 32px",
                background:    GOLD,
                border:        "none",
                borderRadius:  "var(--lwd-radius-input)",
                fontFamily:    "var(--font-body)",
                fontSize:      11,
                fontWeight:    700,
                letterSpacing: "1px",
                textTransform: "uppercase",
                color:         "#0D0B09",
                cursor:        "pointer",
              }}
            >
              Done
            </button>
          </div>
        ) : (
          /* ── Form ──────────────────────────────────────────────────────── */
          <form onSubmit={handleSubmit} style={{ padding: "32px 28px 28px" }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
              <div style={{
                fontFamily:    "var(--font-body)",
                fontSize:      9,
                fontWeight:    700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color:         GOLD,
                marginBottom:  6,
              }}>
                Your Shortlist
              </div>
              <div style={{
                fontFamily:   "var(--font-heading-primary)",
                fontSize:     22,
                fontWeight:   500,
                color:        T.text,
                lineHeight:   1.2,
                marginBottom: 8,
              }}>
                Send Your Brief
              </div>
              <div style={{ fontFamily: "var(--font-body)", fontSize: 12.5, color: T.subtext, lineHeight: 1.55 }}>
                One message sent to all {items.length} venues. Each team will reply to you directly.
              </div>
            </div>

            {/* Venue chips */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
              {items.map((venue) => (
                <div
                  key={venue.id}
                  style={{
                    display:       "flex",
                    alignItems:    "center",
                    gap:           6,
                    padding:       "4px 10px 4px 6px",
                    background:    "rgba(211,154,33,0.08)",
                    border:        `1px solid rgba(211,154,33,0.22)`,
                    borderRadius:  20,
                  }}
                >
                  {venue.imgs?.[0] && (
                    <img
                      src={imgSrc(venue.imgs[0])}
                      alt={venue.name}
                      style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }}
                    />
                  )}
                  <span style={{ fontFamily: "var(--font-body)", fontSize: 10, color: GOLD, fontWeight: 600 }}>
                    {venue.name}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: T.divider, marginBottom: 22 }} />

            {/* Name */}
            <FieldGroup label="Your Name" error={errors.name}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First and last name"
                style={inputStyle(T, !!errors.name)}
              />
            </FieldGroup>

            {/* Email */}
            <FieldGroup label="Email Address" error={errors.email}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={inputStyle(T, !!errors.email)}
              />
            </FieldGroup>

            {/* Wedding date */}
            <FieldGroup label="Approximate Date (optional)">
              <input
                type="text"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="e.g. September 2026"
                style={inputStyle(T, false)}
              />
            </FieldGroup>

            {/* Guest count */}
            <FieldGroup label="Guest Count" error={errors.guests}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {GUEST_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setGuests(opt)}
                    style={{
                      padding:       "5px 12px",
                      borderRadius:  20,
                      border:        `1px solid ${guests === opt ? GOLD : T.inputBorder}`,
                      background:    guests === opt ? "rgba(211,154,33,0.1)" : T.inputBg,
                      fontFamily:    "var(--font-body)",
                      fontSize:      10.5,
                      color:         guests === opt ? GOLD : T.labelColor,
                      cursor:        "pointer",
                      fontWeight:    guests === opt ? 600 : 400,
                      transition:    "all 0.18s",
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {errors.guests && <FieldError text={errors.guests} T={T} />}
            </FieldGroup>

            {/* Message */}
            <FieldGroup label="Your Brief" error={errors.message}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us a little about your wedding vision, what matters most to you, and any questions you have about these venues…"
                rows={4}
                style={{
                  ...inputStyle(T, !!errors.message),
                  resize:   "vertical",
                  minHeight: 90,
                  lineHeight: 1.55,
                }}
              />
              <div style={{
                fontFamily: "var(--font-body)",
                fontSize:   9,
                color:      message.trim().length >= 40 ? "rgba(34,197,94,0.7)" : T.labelColor,
                marginTop:  4,
                textAlign:  "right",
                transition: "color 0.2s",
              }}>
                {message.trim().length}/40 minimum
              </div>
            </FieldGroup>

            {/* Submit error */}
            {errors.submit && (
              <div style={{
                fontFamily:   "var(--font-body)",
                fontSize:     12,
                color:        T.errColor,
                marginBottom: 16,
                padding:      "10px 14px",
                background:   "rgba(248,113,113,0.08)",
                borderRadius: 6,
                border:       `1px solid rgba(248,113,113,0.2)`,
              }}>
                {errors.submit}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === "submitting"}
              style={{
                width:         "100%",
                padding:       "12px 0",
                background:    status === "submitting" ? "rgba(211,154,33,0.5)" : GOLD,
                border:        "none",
                borderRadius:  "var(--lwd-radius-input)",
                fontFamily:    "var(--font-body)",
                fontSize:      11,
                fontWeight:    700,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                color:         "#0D0B09",
                cursor:        status === "submitting" ? "wait" : "pointer",
                transition:    "all 0.2s",
                marginTop:     4,
              }}
            >
              {status === "submitting" ? "Sending…" : `Send to ${items.length} Venue${items.length > 1 ? "s" : ""}`}
            </button>

            {/* Privacy note */}
            <div style={{
              fontFamily: "var(--font-body)",
              fontSize:   10,
              color:      T.subtext,
              textAlign:  "center",
              marginTop:  12,
              opacity:    0.6,
              lineHeight: 1.5,
            }}>
              Your details are shared only with the venues above. We never sell contact information.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Micro-components ─────────────────────────────────────────────────────────

function FieldGroup({ label, error, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display:       "block",
        fontFamily:    "var(--font-body)",
        fontSize:      9,
        fontWeight:    700,
        letterSpacing: "1.2px",
        textTransform: "uppercase",
        color:         "rgba(211,154,33,0.75)",
        marginBottom:  6,
      }}>
        {label}
      </label>
      {children}
      {error && <FieldError text={error} />}
    </div>
  );
}

function FieldError({ text }) {
  return (
    <div style={{
      fontFamily: "var(--font-body)",
      fontSize:   10.5,
      color:      "#f87171",
      marginTop:  4,
    }}>
      {text}
    </div>
  );
}

function inputStyle(T, hasError) {
  return {
    width:           "100%",
    background:      T.inputBg,
    border:          `1px solid ${hasError ? "#f87171" : T.inputBorder}`,
    borderRadius:    6,
    padding:         "9px 12px",
    fontFamily:      "var(--font-body)",
    fontSize:        13,
    color:           T.inputText,
    outline:         "none",
    boxSizing:       "border-box",
    transition:      "border-color 0.18s",
  };
}
